import React, { useState } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import {
  mdiAlertCircleOutline,
  mdiCheckCircleOutline,
  mdiDownload,
  mdiTableLarge,
  mdiTableColumn,
} from '@mdi/js';
import { SimulationResult } from '../types';
import { useUnits, convertForDisplay } from '../units';

/* ────────────────────────── Styled components ────────────────────────── */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  border-bottom: 1px solid #e8eaed;
  background: #fafbfc;
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderTitle = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #202124;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border: 1px solid #dadce0;
  border-radius: 3px;
  background: #fff;
  cursor: pointer;
  font-size: 10px;
  color: #5f6368;
  font-family: inherit;

  &:hover {
    background: #f1f3f4;
    border-color: #c6c8cc;
  }
`;

const CircuitSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 12px;
  border-bottom: 1px solid #f1f3f4;
`;

const CircuitChip = styled.span<{ $ok: boolean }>`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${props => props.$ok ? '#f1f3f4' : '#fce8e6'};
  color: ${props => props.$ok ? '#3c4043' : '#c5221f'};
`;

const StatusBadge = styled.span<{ converged: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 10px;
  background: ${props => props.converged ? '#e6f4ea' : '#fce8e6'};
  color: ${props => props.converged ? '#137333' : '#c5221f'};
`;

const TabBar = styled.div`
  display: flex;
  gap: 1px;
  padding: 0 12px;
  background: #f1f3f4;
  flex-shrink: 0;
  overflow-x: auto;
  align-items: center;
`;

const TabGroup = styled.div`
  display: flex;
  gap: 1px;
  flex: 1;
  overflow-x: auto;
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 2px;
  margin-left: 8px;
  flex-shrink: 0;
  padding: 2px 0;
`;

const ViewToggleButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  border: 1px solid ${props => props.active ? '#1a73e8' : '#dadce0'};
  border-radius: 3px;
  background: ${props => props.active ? '#e8f0fe' : '#fff'};
  color: ${props => props.active ? '#1a73e8' : '#5f6368'};
  cursor: pointer;
  font-size: 10px;
  font-family: inherit;
  font-weight: ${props => props.active ? 600 : 400};

  &:hover {
    background: ${props => props.active ? '#d2e3fc' : '#f1f3f4'};
  }
`;

const ScenarioTab = styled.button<{ active: boolean }>`
  padding: 6px 14px;
  background: ${props => props.active ? '#ffffff' : 'transparent'};
  border: none;
  border-bottom: ${props => props.active ? '2px solid #1a73e8' : '2px solid transparent'};
  cursor: pointer;
  font-size: 11px;
  font-weight: ${props => props.active ? 600 : 400};
  color: ${props => props.active ? '#1a73e8' : '#5f6368'};
  white-space: nowrap;

  &:hover {
    background: ${props => props.active ? '#ffffff' : '#e8eaed'};
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  padding: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
`;

const Th = styled.th`
  padding: 6px 10px;
  text-align: left;
  font-weight: 600;
  color: #5f6368;
  background: #f8f9fa;
  border-bottom: 1px solid #e8eaed;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const ThGroup = styled.th`
  padding: 4px 10px;
  text-align: center;
  font-weight: 700;
  color: #202124;
  background: #f1f3f4;
  border-bottom: 1px solid #e8eaed;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 2;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const ThSub = styled.th`
  padding: 4px 8px;
  text-align: left;
  font-weight: 500;
  color: #5f6368;
  background: #f8f9fa;
  border-bottom: 1px solid #e8eaed;
  white-space: nowrap;
  position: sticky;
  top: 22px;
  z-index: 1;
  font-size: 10px;
`;

const Td = styled.td<{ warning?: boolean; danger?: boolean }>`
  padding: 5px 10px;
  color: #202124;
  border-bottom: 1px solid #f1f3f4;
  white-space: nowrap;
  background: ${props =>
    props.danger ? '#fce8e6' :
    props.warning ? '#fef7e0' :
    'transparent'};
`;

const WarningsList = styled.div`
  padding: 6px 12px;
  border-top: 1px solid #e8eaed;
  flex-shrink: 0;
`;

const WarningItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #e37400;
  padding: 2px 0;
`;

/* ────────────────────────── Types ────────────────────────── */

type ViewMode = 'single' | 'compare';

interface SimulationResultsProps {
  results: SimulationResult[];
  selectedScenarioId: string | null;
  onSelectScenario: (scenarioId: string) => void;
  nodeNames: Record<string, string>;
  nodeThresholds: Record<string, { inlet?: number; outlet?: number }>;
}

/* ────────────────────────── Component ────────────────────────── */

export const SimulationResults: React.FC<SimulationResultsProps> = ({
  results,
  selectedScenarioId,
  onSelectScenario,
  nodeNames,
  nodeThresholds,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('single');
  const units = useUnits();

  const activeResult = results.find(r => r.scenario_id === selectedScenarioId) || results[0];

  const getThresholdStatus = (
    nodeId: string,
    field: 'inlet' | 'outlet',
    value: number
  ): 'danger' | 'warning' | undefined => {
    const thresholds = nodeThresholds[nodeId];
    if (!thresholds) return undefined;
    const limit = thresholds[field];
    if (limit === undefined) return undefined;
    if (value > limit) return 'danger';
    if (value > limit - 5) return 'warning';
    return undefined;
  };

  if (!activeResult) return null;

  // CSV export
  const handleExportCsv = () => {
    const rows: string[][] = [];

    if (viewMode === 'compare' && results.length > 1) {
      // Comparison CSV: one row per component, columns for each scenario
      const header = ['Component'];
      for (const r of results) {
        const name = r.scenario_name || r.scenario_id;
        header.push(
          `${name} - Flow [${units.unit('flow')}]`,
          `${name} - Inlet [${units.unit('temperature')}]`,
          `${name} - Outlet [${units.unit('temperature')}]`,
          `${name} - dP [${units.unit('pressure')}]`,
          `${name} - Thermal [${units.unit('power')}]`,
        );
      }
      rows.push(header);

      const nodeIds = Object.keys(results[0].components);
      for (const nodeId of nodeIds) {
        const row = [nodeNames[nodeId] || nodeId];
        for (const r of results) {
          const comp = r.components[nodeId];
          if (comp) {
            row.push(
              convertForDisplay(comp.flow_rate, 'flow', units.system).toFixed(1),
              convertForDisplay(comp.inlet_temperature, 'temperature', units.system).toFixed(1),
              convertForDisplay(comp.outlet_temperature, 'temperature', units.system).toFixed(1),
              comp.pressure_drop !== undefined ? convertForDisplay(comp.pressure_drop, 'pressure', units.system).toFixed(2) : '',
              comp.thermal_output !== undefined ? convertForDisplay(comp.thermal_output, 'power', units.system).toFixed(2) : '',
            );
          } else {
            row.push('', '', '', '', '');
          }
        }
        rows.push(row);
      }
    } else {
      // Single-scenario CSV for each scenario
      for (const r of results) {
        if (results.length > 1) {
          rows.push([`Scenario: ${r.scenario_name || r.scenario_id}`]);
        }
        rows.push([
          'Component',
          `Flow Rate [${units.unit('flow')}]`,
          `Inlet Temp [${units.unit('temperature')}]`,
          `Outlet Temp [${units.unit('temperature')}]`,
          `Pressure Drop [${units.unit('pressure')}]`,
          `Thermal Output [${units.unit('power')}]`,
        ]);
        for (const [nodeId, comp] of Object.entries(r.components)) {
          rows.push([
            nodeNames[nodeId] || nodeId,
            convertForDisplay(comp.flow_rate, 'flow', units.system).toFixed(1),
            convertForDisplay(comp.inlet_temperature, 'temperature', units.system).toFixed(1),
            convertForDisplay(comp.outlet_temperature, 'temperature', units.system).toFixed(1),
            comp.pressure_drop !== undefined ? convertForDisplay(comp.pressure_drop, 'pressure', units.system).toFixed(2) : '',
            comp.thermal_output !== undefined ? convertForDisplay(comp.thermal_output, 'power', units.system).toFixed(2) : '',
          ]);
        }
        if (results.length > 1) rows.push([]);
      }
    }

    const csv = rows.map(r => r.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'simulation_results.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const componentEntries = Object.entries(activeResult.components);
  const showComparison = viewMode === 'compare' && results.length > 1;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <StatusBadge converged={activeResult.converged}>
            <Icon
              path={activeResult.converged ? mdiCheckCircleOutline : mdiAlertCircleOutline}
              size={0.45}
            />
            {activeResult.converged
              ? `Converged (${activeResult.iterations} iter.)`
              : 'Did not converge'}
          </StatusBadge>
        </HeaderLeft>
        <HeaderActions>
          <HeaderButton
            onClick={(e) => { e.stopPropagation(); handleExportCsv(); }}
            title="Export results to CSV"
          >
            <Icon path={mdiDownload} size={0.45} />
            CSV
          </HeaderButton>
        </HeaderActions>
      </Header>

      {activeResult.circuits && activeResult.circuits.length > 1 && (
        <CircuitSummary data-testid="circuit-summary">
          {activeResult.circuits.map(c => (
            <CircuitChip key={c.id} $ok={c.converged}>
              <strong>{c.name}</strong> · {c.fluid_name} · {units.displayWithUnit(c.flow_rate, 'flow')}
              {' '}· Δp {units.displayWithUnit(c.total_pressure_drop, 'pressure')}
              {!c.converged && ' · did not converge'}
            </CircuitChip>
          ))}
        </CircuitSummary>
      )}

      {results.length > 1 && (
        <TabBar>
          <TabGroup>
            {viewMode === 'single' && results.map((r) => (
              <ScenarioTab
                key={r.scenario_id}
                active={r.scenario_id === (selectedScenarioId || results[0]?.scenario_id)}
                onClick={() => onSelectScenario(r.scenario_id)}
              >
                {r.scenario_name || r.scenario_id}
              </ScenarioTab>
            ))}
            {viewMode === 'compare' && (
              <ScenarioTab active style={{ cursor: 'default' }}>
                All Scenarios
              </ScenarioTab>
            )}
          </TabGroup>
          <ViewToggle>
            <ViewToggleButton
              active={viewMode === 'single'}
              onClick={() => setViewMode('single')}
              title="Single scenario view"
            >
              <Icon path={mdiTableColumn} size={0.4} />
              Single
            </ViewToggleButton>
            <ViewToggleButton
              active={viewMode === 'compare'}
              onClick={() => setViewMode('compare')}
              title="Compare all scenarios"
            >
              <Icon path={mdiTableLarge} size={0.4} />
              Compare
            </ViewToggleButton>
          </ViewToggle>
        </TabBar>
      )}

          <TableContainer>
            {showComparison ? (
              <Table>
                <thead>
                  <tr>
                    <ThGroup rowSpan={2} style={{ textAlign: 'left', verticalAlign: 'bottom' }}>Component</ThGroup>
                    {results.map(r => (
                      <ThGroup key={r.scenario_id} colSpan={7}>
                        {r.scenario_name || r.scenario_id}
                        {!r.converged && ' ⚠'}
                      </ThGroup>
                    ))}
                  </tr>
                  <tr>
                    {results.map(r => (
                      <React.Fragment key={r.scenario_id}>
                        <ThSub>Flow [{units.unit('flow')}]</ThSub>
                        <ThSub>In [{units.unit('temperature')}]</ThSub>
                        <ThSub>Max In [{units.unit('temperature')}]</ThSub>
                        <ThSub>Out [{units.unit('temperature')}]</ThSub>
                        <ThSub>Max Out [{units.unit('temperature')}]</ThSub>
                        <ThSub>dP [{units.unit('pressure')}]</ThSub>
                        <ThSub>Q [{units.unit('power')}]</ThSub>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(results[0].components).map(nodeId => (
                    <tr key={nodeId}>
                      <Td style={{ fontWeight: 500 }}>{nodeNames[nodeId] || nodeId}</Td>
                      {results.map(r => {
                        const comp = r.components[nodeId];
                        const thresholds = nodeThresholds[nodeId];
                        if (!comp) return <React.Fragment key={r.scenario_id}><Td>-</Td><Td>-</Td><Td>-</Td><Td>-</Td><Td>-</Td><Td>-</Td><Td>-</Td></React.Fragment>;
                        return (
                          <React.Fragment key={r.scenario_id}>
                            <Td>{units.display(comp.flow_rate, 'flow')}</Td>
                            <Td
                              warning={getThresholdStatus(nodeId, 'inlet', comp.inlet_temperature) === 'warning'}
                              danger={getThresholdStatus(nodeId, 'inlet', comp.inlet_temperature) === 'danger'}
                            >
                              {units.display(comp.inlet_temperature, 'temperature')}
                            </Td>
                            <Td style={{ color: '#5f6368' }}>{thresholds?.inlet != null ? units.display(thresholds.inlet, 'temperature') : '-'}</Td>
                            <Td
                              warning={getThresholdStatus(nodeId, 'outlet', comp.outlet_temperature) === 'warning'}
                              danger={getThresholdStatus(nodeId, 'outlet', comp.outlet_temperature) === 'danger'}
                            >
                              {units.display(comp.outlet_temperature, 'temperature')}
                            </Td>
                            <Td style={{ color: '#5f6368' }}>{thresholds?.outlet != null ? units.display(thresholds.outlet, 'temperature') : '-'}</Td>
                            <Td>{comp.pressure_drop !== undefined ? units.display(comp.pressure_drop, 'pressure', 2) : '-'}</Td>
                            <Td>{comp.thermal_output !== undefined ? units.display(comp.thermal_output, 'power', 2) : '-'}</Td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Component</Th>
                    <Th>Flow Rate [{units.unit('flow')}]</Th>
                    <Th>Inlet Temp [{units.unit('temperature')}]</Th>
                    <Th>Max Inlet [{units.unit('temperature')}]</Th>
                    <Th>Outlet Temp [{units.unit('temperature')}]</Th>
                    <Th>Max Outlet [{units.unit('temperature')}]</Th>
                    <Th>Pressure Drop [{units.unit('pressure')}]</Th>
                    <Th>Thermal Output [{units.unit('power')}]</Th>
                  </tr>
                </thead>
                <tbody>
                  {componentEntries.map(([nodeId, comp]) => {
                    const thresholds = nodeThresholds[nodeId];
                    return (
                    <tr key={nodeId}>
                      <Td>{nodeNames[nodeId] || nodeId}</Td>
                      <Td>{units.display(comp.flow_rate, 'flow')}</Td>
                      <Td
                        warning={getThresholdStatus(nodeId, 'inlet', comp.inlet_temperature) === 'warning'}
                        danger={getThresholdStatus(nodeId, 'inlet', comp.inlet_temperature) === 'danger'}
                      >
                        {units.display(comp.inlet_temperature, 'temperature')}
                      </Td>
                      <Td style={{ color: '#5f6368' }}>{thresholds?.inlet != null ? units.display(thresholds.inlet, 'temperature') : '-'}</Td>
                      <Td
                        warning={getThresholdStatus(nodeId, 'outlet', comp.outlet_temperature) === 'warning'}
                        danger={getThresholdStatus(nodeId, 'outlet', comp.outlet_temperature) === 'danger'}
                      >
                        {units.display(comp.outlet_temperature, 'temperature')}
                      </Td>
                      <Td style={{ color: '#5f6368' }}>{thresholds?.outlet != null ? units.display(thresholds.outlet, 'temperature') : '-'}</Td>
                      <Td>{comp.pressure_drop !== undefined ? units.display(comp.pressure_drop, 'pressure', 2) : '-'}</Td>
                      <Td>{comp.thermal_output !== undefined ? units.display(comp.thermal_output, 'power', 2) : '-'}</Td>
                    </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </TableContainer>

          {activeResult.warnings.length > 0 && (
            <WarningsList>
              {activeResult.warnings.map((w, i) => (
                <WarningItem key={i}>
                  <Icon path={mdiAlertCircleOutline} size={0.5} />
                  {w}
                </WarningItem>
              ))}
            </WarningsList>
          )}
    </Container>
  );
};
